<template>
    <section
        role="group"
        aria-labelledby="lashesHeading"
        class="cz2__lashes"
    >
        <h2
            id="lashesHeading"
            :class="{ 'outta-stock': hasOutOfStock }"
            class="cz2__lashes-heading"
        >
            {{ $t('STEP_HEAD') }}
        </h2>

        <button
            @click="toggleLashes"
            type="button"
            role="switch"
            :disabled="hasOutOfStock"
            :aria-checked="hasLashes"
            :class="[
                {
                    'has-lashes': hasLashes,
                    'no-lashes': !hasLashes,
                    'has-out-of-stock': hasOutOfStock,
                },
            ]"
            class="cz2__switch-button"
        >
            <span class="cz2__switch-button-indicator" />
        </button>

        <!-- <div
            role="radiogroup"
            aria-labelledby="lashesHeading"
            class="cz2__lashes-list-wrapper hide"
        >
            <selection-card
                v-for="component in availableComponents"
                :key="component.code"
                :code="component.code"
                :image="component.custom['fun-image-v2'] || component.custom['fun-image'] || component.custom.image"
                :name="component.custom['fun-title']"
                :component="component"
                @select="select(component)"
                :isSelected="isSelected(component)"
                :isOutOfStock="isOutOfStock(component)"
            />
        </div> -->
    </section>
</template>

<style lang="scss">
    @import "../style/variables.scss";

    #cz2.cz2 {
        .cz2__lashes {
            display: flex;
            align-items: center;
            gap: 12px;

            .cz2__lashes-heading {
                @include font(h1);
                color: color(primaryWhite);
                text-transform: uppercase;

                &.outta-stock {
                    color: #8DBAED;
                }
            }

            .cz2__switch-button {
                border-radius: 750px;
                border: 2px solid white;
                width: 64px;
                height: 24px;
                background-color: white;
                position: relative;
                transition: all 0.2s ease-in-out;

                .cz2__switch-button-indicator {
                    position: absolute;
                    top: 0;
                    transform: translateX(calc(-100% + 2px));
                    transition: all 0.2s ease-in-out;

                    border-radius: 1000px;
                    width: 32px;
                    height: 20px;
                    background-color: #033F70;
                }

                &.has-lashes {
                    .cz2__switch-button-indicator {
                        transform: translateX(calc(100% - 34px));
                    }
                }

                &.no-lashes,
                &.has-out-of-stock {
                    border-color: #8DBAED;
                    background-color: #8DBAED;
                }

                &.has-out-of-stock {
                    cursor: not-allowed;
                }
            }

            .cz2__lashes-list-wrapper {
                margin-top: 8px;
                display: flex;
                gap: 16px;

                .selection-card {
                    width: 75px;
                }
            }
        }

        &.cz2__is-kiosk {
            .cz2__lashes {
                .cz2__switch-button {
                    width: 104px;
                    height: 40px;

                    .cz2__switch-button-indicator {
                        top: 2px;
                        transform: translateX(calc(-100% + 1px));
                        width: 48px;
                        height: 32px;
                    }

                    &.has-lashes {
                        .cz2__switch-button-indicator {
                            transform: translateX(calc(100% - 48px));
                        }
                    }
                }
            }
        }
    }
</style>

<script>
    import SelectionCard from './ui/SelectionCard.vue';
    import Swiper from './ui/Swiper.vue';

    export default {
        components: {
            SelectionCard,
            Swiper,
        },

        computed: {
            viewController() {
                return this.$store.state.customizer.viewController;
            },

            allGroups() {
                return this.viewController.blueprint.groups;
            },

            lashesGroup() {
                return this.allGroups.filter((group) => group.code === 'Head')[0];
            },

            editPlacement() {
                if (this.viewController == null) {
                    return null;
                }

                if (this.lashesGroup.placements == null || this.lashesGroup.placements.length === 0) {
                    return null;
                }

                return this.lashesGroup.placements[0];
            },

            availableComponents() {
                const placement = this.editPlacement;

                if (placement == null) {
                    return [];
                }

                const components = placement.components.filter((c) => !c.placeholder && this.viewController.isAllowed(c));
                const inStockComponents = components.filter((c) => !this.isOutOfStock(c));
                const oosComponents = components.filter((c) => this.isOutOfStock(c));

                return inStockComponents.concat(oosComponents);
            },

            selectedComponent() {
                const placement = this.editPlacement;

                if (placement == null) {
                    return null;
                }

                const selected = this.viewController.selectedAtPlacement(placement.code);

                return selected && !selected.placeholder ? selected : null;
            },

            hasLashes() {
                return this.selectedComponent?.code?.toLowerCase().includes('fm') ?? false;
            },

            isFilled() {
                return this.selectedComponent != null;
            },

            hasOutOfStock() {
                // Check if any available component is out of stock. Used to disable switch button.
                return this.availableComponents.filter((c) => this.isOutOfStock(c)).length > 0;
            },
        },

        mounted() {
            this.updateNavigation();
        },

        methods: {
            isSelected(component) {
                return this.selectedComponent && this.selectedComponent.code === component.code;
            },

            isOutOfStock(component) {
                return this.viewController.isAllowed(component) && !this.viewController.isAvailable(component);
            },

            select(component) {
                const placement = this.editPlacement;

                if (placement != null) {
                    this.$store.dispatch('updateComponent', {
                        placement: placement.code,
                        code: component.code,
                    }).then(() => {
                        // Is this still available?
                        // The head may have already been disabled by a follow up component.
                        // This happens when selecting Buzz Cut -> then backtracking and switching lashes again.
                        if (!this.viewController.isAvailable(component)) {
                            // Whelp if not... try to find an acceptable head?
                            // Pick a new head while preserving lashes state.
                            const hasLashes = component.tags?.includes('lashes');

                            // eslint-disable-next-line no-restricted-syntax
                            for (const c of placement.components) {
                                if (!this.viewController.isAvailable(c)) {
                                    // eslint-disable-next-line no-continue
                                    continue;
                                }

                                const lashes = c.tags?.includes('lashes');

                                if (lashes === hasLashes) {
                                    if (this.viewController.updateComponent(placement.code, c.code)) {
                                        break;
                                    }
                                }
                            }
                        }
                    });
                }

                this.updateNavigation();
            },

            toggleLashes() {
                // Select the component that is not currently selected
                const unselectedComponent = this.availableComponents.find((c) => !this.isSelected(c));

                this.select(unselectedComponent);
            },

            updateNavigation() {
                this.$emit('updateNavigation', {
                    // Must be filled.
                    canGoNext: this.isFilled,
                });
            },
        },
    };
</script>
